import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { AbsenceDay } from '../../../../types/absence-day'
import { showErrorMessage } from '../../../../redux/reducers/snackbarReducer'
import { SickHoursValidationHook } from '../types/validationTypes'

export const useSickHoursValidation = (absenceDays: AbsenceDay[]): SickHoursValidationHook => {
  const [sickHoursMap, setSickHoursMap] = useState<Map<number, number>>(new Map())
  const [validationErrorsMap, setValidationErrorsMap] = useState<Map<number, any>>(new Map())
  const [disabledMap, setDisabledMap] = useState<Map<number, boolean>>(new Map())

  const dispatch = useDispatch()

  const initializeSickHours = useCallback((days: AbsenceDay[]) => {
    const newSickHoursMap = new Map()
    const newDisabledMap = new Map()

    days.forEach((day: AbsenceDay) => {
      newSickHoursMap.set(day.id, day.sickHoursToBePaid || 0)
      newDisabledMap.set(day.id, day.sickHoursToBePaid === (day.sickHoursToBePaid || 0))
    })

    setSickHoursMap(newSickHoursMap)
    setDisabledMap(newDisabledMap)
  }, [])

  useEffect(() => {
    initializeSickHours(absenceDays)
  }, [absenceDays, initializeSickHours])

  const updateSickHours = useCallback(
    (rowId: number, newValue: number) => {
      setSickHoursMap(prev => new Map(prev.set(rowId, newValue)))

      const row = absenceDays.find(day => day.id === rowId)
      if (row) {
        setDisabledMap(prev => new Map(prev.set(rowId, row.sickHoursToBePaid === newValue)))
      }
    },
    [absenceDays]
  )

  const isValidForRow = useCallback(
    (rowId: number): boolean => {
      const row = absenceDays.find(day => day.id === rowId)
      const sickHours = sickHoursMap.get(rowId) || 0

      if (!row) return false

      setValidationErrorsMap(prev => {
        const newMap = new Map(prev)
        newMap.delete(rowId)
        return newMap
      })

      const errors: any = {}
      const existingSickHoursToBePaid = row.sickHoursToBePaid ?? 0
      const availablePay = Number(row.sickPayHoursRemaining + existingSickHoursToBePaid)

      if (sickHours < existingSickHoursToBePaid) {
        // Always allow reducing existing payment
      } else if (sickHours > availablePay) {
        errors.sickPayHoursRemaining = true
        dispatch(
          showErrorMessage(
            `Sick hours of ${sickHours} is greater than ${availablePay} hours pay available for day`
          )
        )
      }

      if (sickHours > row.hoursLost) {
        errors.hoursLost = true
        dispatch(
          showErrorMessage(
            `Sick hours of ${sickHours} is greater than the ${row.hoursLost} hours lost for the day`
          )
        )
      }

      if (Object.keys(errors).length !== 0) {
        setValidationErrorsMap(prev => new Map(prev.set(rowId, errors)))
        return false
      }

      return true
    },
    [absenceDays, sickHoursMap, dispatch]
  )

  return {
    sickHoursMap,
    validationErrorsMap,
    disabledMap,

    updateSickHours,
    isValidForRow,
    initializeSickHours,
  }
}
